import { Component, OnInit, OnDestroy, inject, input, output, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BillAiFlowStore } from '../state/bill-ai-flow.store';
import { BillNavEntry } from '../state/bill-ai-flow.models';
import { BillFile, ParsedBillData, ChargeGroup, MismatchWarning } from '../../../models';
import { CommodityType } from '../../../../zentility/_shared/_zen-legacy-common/_models/commodity';
import { BillMergeService } from '../../../services/bill-merge.service';
import { BillMappingService } from '../../../services/bill-mapping.service';
import { BillMergeResponse, MergeDecision } from '../../../services/api-models';
import { BillService } from '../../../services/bill.service';
import { BillPersistenceService } from '../../../services/bill-persistence.service';
import { BillsV4ElecService } from '../../../../zentility/_shared/_services/v4/bills-v4-elec.service';
import { BillsV4GasService } from '../../../../zentility/_shared/_services/v4/bills-v4-gas.service';
import { CommodityBadgeComponent } from '../../shared/commodity-badge.component';
import { BillType, PhysicalBillType } from '../../../../zentility/_shared/_model/bills-v4.model';
import { ElecBillDto } from '../../../../zentility/_shared/_model/bills-v4-elec.model';
import { GasBillDto } from '../../../../zentility/_shared/_model/bills-v4-gas.model';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DuplicateAction, DuplicateWarning, ExistingFileType } from '../../../models/bill-import.models';
import { ExistingBillPdfService } from '../../../services/existing-bill-pdf.service';
import { buildDuplicateBanner, effectiveDuplicateAction, existingPdfLinkLabel, uploadBillSides } from '../state/duplicate-resolution.util';
import { BillStatus } from '../../../models';
import { HttpClient } from '@angular/common/http';
import { UnitService } from '../../../../zentility/_shared/_services/v4/unit-service';
import { UnitDto } from '../../../../zentility/_shared/_model/unit-model';
import { take } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';
import { validateBillReviewStep, resolveMeterContext, isDualHalf } from './bill-ai-flow-bill-review-step.validation';
import { detectContextMismatches } from './bill-ai-flow-context-mismatch.util';
import { StepValidationResult, ValidationSummary, groupErrorsBySummary } from '../validation';
import { BillFormComponent, BillFormMeterContext, createEmptyParsedBillData } from '../../shared/bill-form/bill-form.component';
import { pickProviderSource } from '../../../utils/bill-utils';

// Re-exported from the validation module (moved there so the store can share it
// without a store ↔ step circular import). Kept here for existing importers.
export { resolveMeterContext };

/** The banner above the bill form when its dates match an existing bill. */
interface DuplicateBanner {
  label: string;
  message: string;
  blocking: boolean;
  pairable?: boolean;
  icon: string;
  /** Exact duplicates only: amber while skipped, danger while replacing. */
  tone?: 'skip' | 'replace';
  existingBillId?: number;
  actionLabel?: string | null;
  actionTarget?: DuplicateAction | null;
  pdfLinks?: { type: ExistingFileType; label: string }[];
}

/**
 * Wizard step that hosts the reusable <app-bill-form> and keeps all
 * store-driven orchestration: bill navigation, duplicate/mismatch banners,
 * dual-bill merge, validation, and persistence via the BillAiFlowStore.
 */
@Component({
  selector: 'app-bill-ai-flow-bill-review-step',
  standalone: true,
  imports: [CommonModule, BillFormComponent, MatRadioModule, MatTooltipModule, CommodityBadgeComponent],
  templateUrl: './bill-ai-flow-bill-review-step.component.html',
  styleUrl: './bill-ai-flow-bill-review-step.component.scss'
})
export class BillAiFlowBillReviewStepComponent implements OnInit, OnDestroy {
  readonly showBackToOverview = input(false);
  readonly backToOverview = output<void>();

  private readonly billFormRef = viewChild(BillFormComponent);

  /** Deep-copied bill data handed to the embedded form (reassigned per bill). */
  billDataForForm: ParsedBillData | null = null;
  /** Minimal entity context for the form's energy-plan lookups, derived from the store. */
  formMeterContext: BillFormMeterContext | null = null;
  /** Commodity shown beside Billing Method: the bill's own, else its meter's (reassigned per bill). */
  billCommodity: CommodityType | undefined = undefined;
  /** Latest form data mirrored from the form's formChanged output (save fallback). */
  private latestFormData: ParsedBillData | null = null;

  // Pre-computed navigation state
  currentBillNumber = 0;
  totalBillCount = 0;
  currentMeterNumber = 0;
  totalMeterCount = 0;
  isFirstBill = true;
  isLastBill = true;
  isLastBillInMeter = false;
  nextButtonLabel = 'Continue';

  // Current context
  currentNav: BillNavEntry | null = null;
  private currentBillFile: BillFile | null = null;

  // Entity mismatch warnings (when bill data doesn't match onboarding context)
  mismatchWarnings: MismatchWarning[] = [];

  // Duplicate bill warning
  duplicateWarning: DuplicateBanner | null = null;

  // Billing method (Consolidated / Dual). Dual re-enables the Supply/Distribution
  // toggles in budgeting mode. A detected pair (two files) or an accepted pair
  // suggestion (merges into an existing bill) can't go Consolidated.
  billingMethod: BillType = BillType.CONSOLIDATED;
  consolidatedDisabled = false;
  readonly BillType = BillType;
  private destroyed = false;

  // Dual bill merge state
  isDualBill = false;
  isMerging = false;
  mergeWarnings: string[] = [];
  mergeDecisions: Record<string, MergeDecision> = {};
  utilityFileName = '';
  supplierFileName = '';
  private pairedBillFile: BillFile | null = null;
  /** In-flight server merge — dropped when another bill loads so its result can't land on that bill. */
  private mergeSub?: Subscription;

  readonly state = inject(BillAiFlowStore);
  private billMergeService = inject(BillMergeService);
  private billMappingService = inject(BillMappingService);
  public billService = inject(BillService);
  private persistenceSvc = inject(BillPersistenceService);
  private billsV4ElecService = inject(BillsV4ElecService);
  private billsV4GasService = inject(BillsV4GasService);
  private http = inject(HttpClient);
  private existingPdfs = inject(ExistingBillPdfService);
  private unitService = inject(UnitService);

  /** Bill groupIds whose server-pair bill has been fetched and applied. */
  private loadedPairBills = new Set<string>();
  /** Pre-merge form data per groupId so "Don't pair" can restore the original extraction. */
  private pairSnapshots = new Map<string, ParsedBillData>();
  /** Pair-bill fetches — torn down on destroy so callbacks can't run after the step is gone. */
  private pairSubs = new Subscription();

  /** Platform units — let rate-bound messages use the bill's unit (e.g. MWh). */
  private allUnits: UnitDto[] = [];
  /** getUnits subscription — torn down on destroy so its callback can't run after the step is gone. */
  private unitsSub?: Subscription;

  ngOnInit(): void {
    // The meter step is the only place that builds bill navigation, but it's
    // skipped at METER-level entry — leaving the Bills step at "Bill 0 of 0"
    // with a blank form until a step change rebuilds it. Build it here when it's
    // empty (buildBillNavigation no-ops until the match response is ready).
    if (this.state.getBillNavigation().length === 0) {
      this.state.buildBillNavigation();
    }
    this.loadCurrentBill();
    // Units feed the rate-bound validator's unit-aware bounds/labels. Cached in
    // UnitService, so re-validate once they arrive in case a bill is already loaded.
    this.unitsSub = this.unitService.getUnits().pipe(take(1)).subscribe({
      next: units => {
        this.allUnits = units || [];
        this.revalidate();
      },
      // Don't swallow a units-load failure silently: rate bounds would quietly
      // fall back to platform (kWh) values. Validation still runs without units.
      error: (err) => console.warn('[BillReviewStep] failed to load units; rate bounds use defaults', err),
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.unitsSub?.unsubscribe();
    this.pairSubs.unsubscribe();
    this.saveFormData();
  }

  // ===========================================================================
  // Bill Loading
  // ===========================================================================

  private loadCurrentBill(): void {
    this.mergeSub?.unsubscribe();
    this.isMerging = false;
    this.currentNav = this.state.getCurrentBillNav();
    const billFile = this.state.getCurrentBillFile();
    const groupId = this.currentNav?.billGroupId;
    const billData = groupId ? this.state.getBillData(groupId) : null;

    // Detect dual billing
    this.isDualBill = !!this.currentNav?.isDualBill;
    const pairedBillFileId = this.currentNav?.pairedBillFileId || null;
    this.pairedBillFile = pairedBillFileId ? this.state.getBillFile(pairedBillFileId) : null;
    this.utilityFileName = billFile?.file?.name || 'Utility Bill';
    this.supplierFileName = this.pairedBillFile?.file?.name || 'Supplier Bill';
    this.mergeWarnings = [];
    this.mergeDecisions = {};

    if (billData) {
      this.currentBillFile = billFile;
      this.billDataForForm = JSON.parse(JSON.stringify(billData));
    } else {
      console.warn('[BillReview] No bill data found for nav entry:', {
        navGroupId: this.currentNav?.billGroupId,
        hasBillFile: !!billFile,
        billFileStatus: billFile?.status,
        isDualBill: this.isDualBill
      });
      this.currentBillFile = null;
      this.billDataForForm = null;
    }
    this.latestFormData = this.billDataForForm;

    this.detectDuplicate(groupId);
    const pairAccepted = !!this.duplicateWarning?.pairable;
    this.billingMethod = this.isDualBill || pairAccepted || isDualHalf(this.billDataForForm ?? undefined)
      ? BillType.DUAL : BillType.CONSOLIDATED;
    this.consolidatedDisabled = this.isDualBill || pairAccepted;
    this.detectMismatches();
    this.updateNavState();
    // The bill's own commodity first (a dual-fuel PDF yields one bill per commodity),
    // then the file row's (set when the PDF was processed), then the meter's from the
    // match context — the last is missing when the match has not happened or the
    // meter is not in it, which must not leave the badge blank.
    this.billCommodity = this.billDataForForm?.commodityType
      ?? billFile?.commodityType
      ?? (this.currentNav ? this.getMeterContext(this.currentNav.meterGroupId)?.commodityType : undefined);
    // The form follows the badge, so a gas bill on an unmatched meter still gets gas units.
    this.formMeterContext = this.buildFormMeterContext(this.billCommodity);
    this.revalidate();

    // If dual bill, merge the two bills' data
    if (this.isDualBill && this.pairedBillFile?.data) {
      this.mergeDualBills();
    }
  }

  private detectDuplicate(groupId: string | undefined): void {
    this.duplicateWarning = null;
    if (!groupId) return;
    let dup = this.state.getBillDuplicate(groupId);
    if (!dup) return;
    // A declined pair suggestion renders as a plain blocking overlap.
    if (dup.pairable && this.state.isPairRejected(groupId)) {
      dup = { ...dup, pairable: false };
    }

    const dates = `${dup.existingStartDate} – ${dup.existingEndDate}`;
    const blocking = dup.overlapType === 'PARTIAL' || dup.overlapType === 'CONTAINS' || dup.overlapType === 'CONTAINED_BY';

    if (dup.pairable) {
      const half = dup.pairSide === 'DISTRIBUTION' ? 'utility' : 'supplier';
      const otherSide = dup.pairSide === 'DISTRIBUTION' ? 'supply' : 'distribution';
      this.duplicateWarning = {
        label: 'Pairs with existing bill',
        message: `This looks like the ${half} half of existing bill #${dup.existingBillId} (${dates}). `
          + `It will be merged into that bill on import; the ${otherSide} values shown come from the existing bill.`,
        blocking: false,
        pairable: true,
        icon: 'link',
      };
      this.loadExistingPairBill(groupId, dup);
      return;
    }
    if (blocking) {
      this.duplicateWarning = {
        label: 'Overlapping',
        message: `This bill's dates overlap with an existing bill (${dates}). Must be resolved before importing.`,
        blocking: true,
        icon: 'block',
      };
    } else {
      // Exact duplicate: the banner follows the Skip / Replace choice and lets the user flip it
      const action = effectiveDuplicateAction(dup, this.state.duplicateResolutions()[groupId]);
      const banner = buildDuplicateBanner(dup, action, uploadBillSides(this.state.getBillData(groupId)));
      this.duplicateWarning = {
        label: banner.title,
        message: banner.message,
        blocking: false,
        icon: banner.icon,
        tone: banner.tone,
        existingBillId: dup.existingBillId,
        actionLabel: banner.actionLabel,
        actionTarget: banner.actionTarget,
        pdfLinks: banner.files.map(type => ({ type, label: existingPdfLinkLabel(type, banner.files.length, true) })),
      };
    }
  }

  /** Flip an exact duplicate between Skip and Replace from its banner. */
  switchDuplicateAction(): void {
    const groupId = this.currentNav?.billGroupId;
    const warning = this.duplicateWarning;
    if (!groupId || !warning?.actionTarget || warning.existingBillId == null) return;
    this.state.setDuplicateResolution(groupId, warning.existingBillId, warning.actionTarget);
    this.detectDuplicate(groupId);
    // A skipped bill isn't validated, so the choice changes which errors apply
    this.revalidate();
  }

  /** Open the existing bill's stored PDF in a new tab. */
  openExistingPdf(type: ExistingFileType): void {
    const groupId = this.currentNav?.billGroupId;
    const id = this.duplicateWarning?.existingBillId;
    if (!groupId || id == null) return;
    this.existingPdfs.open(this.state.getExistingBillRef(groupId), id, type);
  }

  /** Decline the pairable suggestion — the bill becomes a plain blocking overlap
   *  and the form reverts to its own extraction (the prefilled other side is undone). */
  rejectPairing(): void {
    const groupId = this.currentNav?.billGroupId;
    if (!groupId) return;
    const snapshot = this.pairSnapshots.get(groupId);
    if (snapshot) {
      const restored: ParsedBillData = JSON.parse(JSON.stringify(snapshot));
      this.billDataForForm = restored;
      this.billFormRef()?.resetTo(restored);
      this.persistMergedData(restored);
      this.pairSnapshots.delete(groupId);
    }
    this.loadedPairBills.delete(groupId);
    this.state.rejectPairSuggestion(groupId);
    this.detectDuplicate(groupId);
  }

  /**
   * Scenario C: this bill is the missing half of an already-imported DUAL bill.
   * Fetch that bill so the form shows the full merged picture (the other side's
   * values) and its PDF shows in the matching tab. Display only — import sends
   * just this bill's own side; the merge happens server-side.
   */
  private loadExistingPairBill(groupId: string, dup: DuplicateWarning): void {
    if (this.loadedPairBills.has(groupId)) return;
    const ids = this.resolveExistingIds(groupId);
    if (!ids) {
      console.error('[BillReviewStep] Cannot load pair bill: unresolved SA/meter ids for group', groupId);
      this.downgradePairBanner(groupId);
      return;
    }

    // The existing half lives in the meter's commodity table — gas bills have their own endpoints.
    const meterGroupId = this.state.billNavigation().find(n => n.billGroupId === groupId)?.meterGroupId;
    const commodity = meterGroupId ? this.getMeterContext(meterGroupId)?.commodityType : undefined;
    const isGas = commodity === CommodityType.Gas;
    const existingBill$: Observable<ElecBillDto | GasBillDto> = isGas
      ? this.billsV4GasService.getBillDetailsById(ids.customerId, ids.saId, ids.meterId, dup.existingBillId)
      : this.billsV4ElecService.getBillDetailsById(ids.customerId, ids.saId, ids.meterId, dup.existingBillId);

    this.pairSubs.add(
      existingBill$
        .subscribe({
          next: dto => this.applyExistingBillData(groupId, dto, dup),
          // Not marked loaded — a transient failure retries on the next visit,
          // and the banner stops promising values that never arrived.
          error: err => {
            console.error('Failed to load existing pair bill', dup.existingBillId, err);
            this.downgradePairBanner(groupId);
          },
        }));

    const slot = dup.pairSide === 'DISTRIBUTION' ? PhysicalBillType.SUPPLIER : PhysicalBillType.UTILITY;
    const url = isGas
      ? this.billsV4GasService.downloadBillByTypeUrl(ids.customerId, ids.saId, ids.meterId, dup.existingBillId, slot)
      : this.billsV4ElecService.downloadBillByTypeUrl(ids.customerId, ids.saId, ids.meterId, dup.existingBillId, slot);
    this.pairSubs.add(
      this.http.get(url, { responseType: 'blob' }).subscribe({
        next: blob => {
          const file = new File([blob], `bill-${dup.existingBillId}.pdf`, { type: 'application/pdf' });
          const billFile: BillFile = {
            id: `existing-${dup.existingBillId}`,
            file,
            previewUrl: '',
            status: BillStatus.APPROVED,
            data: null,
          };
          this.state.attachServerPairBill(groupId, billFile, dup.pairSide !== 'DISTRIBUTION');
        },
        error: err => {
          if (err?.status === 404) {
            console.warn('Existing pair bill has no PDF to preview', dup.existingBillId);
          } else {
            console.error('Failed to fetch existing pair bill PDF', dup.existingBillId, err?.status, err);
          }
        },
      }));
  }

  /** The merged picture couldn't be loaded — stop the banner claiming it's on screen. */
  private downgradePairBanner(groupId: string): void {
    if (this.currentNav?.billGroupId !== groupId || !this.duplicateWarning?.pairable) return;
    this.duplicateWarning = {
      ...this.duplicateWarning,
      message: 'This looks like the missing half of an existing bill, but the existing bill\'s values could not be loaded. Only this document\'s side is shown; the merge still happens on import.',
    };
  }

  /** Fill the other side of the form from the existing bill's stored values. */
  private applyExistingBillData(groupId: string, dto: any, dup: DuplicateWarning): void {
    if (this.currentNav?.billGroupId !== groupId) return;
    const existing = this.persistenceSvc.toParsedBillData(dto);
    const current = this.billFormRef()?.getFormData() ?? this.latestFormData ?? this.billDataForForm;
    if (!current) {
      console.error('[BillReviewStep] Cannot apply pair bill data: no current form data for group', groupId);
      this.downgradePairBanner(groupId);
      return;
    }
    this.loadedPairBills.add(groupId);
    // Keep the pre-merge extraction so "Don't pair" can restore it.
    if (!this.pairSnapshots.has(groupId)) {
      this.pairSnapshots.set(groupId, JSON.parse(JSON.stringify(current)));
    }

    const merged: ParsedBillData = JSON.parse(JSON.stringify(current));
    if (dup.pairSide !== 'DISTRIBUTION') {
      // Incoming supplier half — distribution, dates and usage come from the
      // existing utility bill (utility data is authoritative for those).
      for (const key of Object.keys(existing) as (keyof ParsedBillData)[]) {
        if (String(key).startsWith('distribution')) (merged as any)[key] = (existing as any)[key];
      }
      merged.distributionDisabled = false;
      if (existing.billingPeriodStart) merged.billingPeriodStart = existing.billingPeriodStart;
      if (existing.billingPeriodEnd) merged.billingPeriodEnd = existing.billingPeriodEnd;
      if (existing.usageAmount != null) merged.usageAmount = existing.usageAmount;
      if (existing.demandKw != null) merged.demandKw = existing.demandKw;
    } else {
      // Incoming utility half — supply AND supplier identity come from the
      // existing supplier bill (the incoming bill may reprint supplier charges,
      // but the supplier half is authoritative; it also isn't "On Utility").
      for (const key of Object.keys(existing) as (keyof ParsedBillData)[]) {
        if (String(key).startsWith('supply')) (merged as any)[key] = (existing as any)[key];
      }
      merged.supplyDisabled = false;
      // Only a real supplier on the existing bill replaces the incoming one;
      // a supplier-less legacy bill maps to the "On Utility" placeholder
      if (existing.supplierId) {
        merged.supplierId = existing.supplierId;
        if (existing.providerName) merged.providerName = existing.providerName;
      }
      if (existing.energyPlanId != null) merged.energyPlanId = existing.energyPlanId;
      if (existing.rateType) merged.rateType = existing.rateType;
      if (existing.rateTypeConfidence != null) merged.rateTypeConfidence = existing.rateTypeConfidence;
      if (existing.contractEndDate) merged.contractEndDate = existing.contractEndDate;
    }
    const supplyTotal = merged.supplyTotalCost ?? ((merged.supplyCharges || 0) + (merged.supplyTaxes || 0));
    const distTotal = merged.distributionTotalCost ?? ((merged.distributionCharges || 0) + (merged.distributionTaxes || 0));
    merged.totalAmount = (supplyTotal || 0) + (distTotal || 0);
    merged.financialsDisabled = false;

    // resetTo (not applyMergedData) so the form re-derives the section toggles —
    // the filled-in side must arrive switched ON or the reconcile check reports
    // a phantom gap against the combined total.
    this.billDataForForm = merged;
    this.billFormRef()?.resetTo(merged);
    this.persistMergedData(merged);
  }

  /** Resolved customer/SA/meter ids for the bill's meter (all existing for a pairable bill). */
  private resolveExistingIds(groupId: string): { customerId: number; saId: number; meterId: number } | null {
    const resp = this.state.matchResponse();
    const customerId = this.state.selectedCustomerId() ?? this.state.getContext()?.customerId;
    if (!resp || !customerId) return null;
    for (const len of resp.legalEntities) {
      for (const sa of len.serviceAddresses) {
        for (const meter of sa.meters) {
          if (meter.bills.some(b => b.groupId === groupId)) {
            return sa.resolvedId != null && meter.resolvedId != null
              ? { customerId: Number(customerId), saId: sa.resolvedId, meterId: meter.resolvedId }
              : null;
          }
        }
      }
    }
    return null;
  }

  /**
   * Compares extracted bill data against the known onboarding context and
   * surfaces a warning per field (LEN, service address, or meter UAN) that
   * doesn't match the parent the bill is being added under.
   */
  private detectMismatches(): void {
    this.mismatchWarnings = detectContextMismatches(this.state.getContext(), this.currentNav);
  }

  /**
   * Merges a utility + supplier bill pair into a single form.
   *
   * Strategy:
   * 1. If both bills have rawExtraction → call server merge API (most accurate)
   * 2. Otherwise → client-side merge using ParsedBillData directly:
   *    - Dates, usage, demand, entity info, distribution → from utility bill
   *    - Supply charges, rate info → from supplier bill
   *    - Provider → pickProviderSource (resolved supplierId beats a bare name)
   *    - Total recalculated from merged supply + distribution
   */
  private mergeDualBills(): void {
    const utilityExtraction = this.currentBillFile?.rawExtraction;
    const supplierExtraction = this.pairedBillFile?.rawExtraction;

    // Path 1: Server-side merge when raw extractions are available
    if (utilityExtraction && supplierExtraction) {
      this.isMerging = true;
      this.mergeSub = this.billMergeService.merge({ utilityExtraction, supplierExtraction })
        .pipe(take(1))
        .subscribe({
          next: (response: BillMergeResponse) => {
            this.isMerging = false;
            this.mergeWarnings = response.warnings || [];
            this.mergeDecisions = response.decisions || {};

            const mergedData = this.billMappingService.mapApiResponseToParsedBillData(response.merged);
            const form = this.billFormRef();
            const before = form ? form.getFormData() : this.billDataForForm;
            mergedData.energyPlanId = before?.energyPlanId;
            // The record being re-merged already carries the pair's provider;
            // a server result without one must not reset it to "On Utility"
            const providerSource = before && pickProviderSource(mergedData, before);
            if (providerSource) {
              mergedData.providerName = providerSource.providerName;
              mergedData.supplierId = providerSource.supplierId;
            }

            if (form) {
              // The HTTP response always lands after the form has initialized.
              form.applyMergedData(mergedData);
            } else {
              this.billDataForForm = mergedData;
              this.latestFormData = mergedData;
            }
            this.persistMergedData(mergedData);
          },
          error: (err) => {
            this.isMerging = false;
            console.warn('Server merge failed, falling back to client-side merge:', err);
            this.clientSideMergeIntoForm();
          }
        });
      return;
    }

    // Path 2: Client-side merge from ParsedBillData. This branch runs
    // synchronously from loadCurrentBill — the form has not (re)initialized
    // from the new billData input yet, so merge into that object directly.
    if (!this.billDataForForm) {
      this.billDataForForm = createEmptyParsedBillData();
      this.latestFormData = this.billDataForForm;
    }
    if (this.applyClientSideMerge(this.billDataForForm)) {
      this.persistMergedData(this.billDataForForm);
    }
  }

  /**
   * A merged pair is one record: write the merged result back to the store
   * so step validation (which reads billData) sees the combined bill, then
   * re-run validation to clear stale one-sided errors.
   */
  private persistMergedData(merged: ParsedBillData): void {
    const groupId = this.currentNav?.billGroupId;
    if (!groupId) return;
    this.state.updateBillData(groupId, JSON.parse(JSON.stringify(merged)));
    this.revalidate();
  }

  /**
   * Async fallback when the server merge fails: merge into the form's
   * current data and push the result back into the form.
   */
  private clientSideMergeIntoForm(): void {
    const form = this.billFormRef();
    if (!form) return;
    const merged: ParsedBillData = JSON.parse(JSON.stringify(form.getFormData()));
    if (this.applyClientSideMerge(merged)) {
      form.applyMergedData(merged);
      this.persistMergedData(merged);
    }
  }

  /**
   * Client-side merge: combines utility bill (distribution) with supplier bill (supply).
   * Utility bill is the base; supplier bill's supply charges overwrite utility's supply fields.
   * Mutates `base` in place; returns false when there is no supplier data to merge.
   */
  private applyClientSideMerge(base: ParsedBillData): boolean {
    const supplierData = this.pairedBillFile?.data;
    if (!supplierData) return false;

    // Supply charges from supplier bill
    if ((supplierData.supplyCharges || 0) > 0 || (supplierData.supplyTotalCost || 0) > 0) {
      base.supplyCharges = supplierData.supplyCharges || 0;
      base.supplyTaxes = supplierData.supplyTaxes || 0;
      base.supplyTotalCost = supplierData.supplyTotalCost || 0;
    }

    // Supply charge group details from supplier bill (pro mode)
    const supplyGroupFields: (keyof ParsedBillData)[] = [
      'supplyContractCharges', 'supplyEnergyCharges', 'supplyCapacityCharges',
      'supplyTransmissionCharges', 'supplyMarketCharges', 'supplyLineLossCharges',
      'supplyGrtSut', 'supplyMiscCharges', 'supplySalesTaxes'
    ];
    for (const field of supplyGroupFields) {
      const supplierGroup = supplierData[field] as ChargeGroup | undefined;
      if (supplierGroup && (supplierGroup.total || 0) > 0) {
        (base as any)[field] = JSON.parse(JSON.stringify(supplierGroup));
      }
    }

    // Same provider precedence as mergePairedBillData — this re-runs on an
    // already merged record, so a bare name must not erase a resolved supplier
    const providerSource = pickProviderSource(supplierData, base);
    if (providerSource) {
      base.providerName = providerSource.providerName;
      base.supplierId = providerSource.supplierId;
    }

    // Recalculate total from merged supply + distribution
    const sc = base.supplyCharges || 0;
    const st = base.supplyTaxes || 0;
    const dc = base.distributionCharges || 0;
    const dt = base.distributionTaxes || 0;
    base.totalAmount = Math.round((sc + st + dc + dt) * 100) / 100;

    return true;
  }

  private updateNavState(): void {
    const idx = this.state.currentBillIndex();
    const total = this.state.getTotalBills();

    this.currentBillNumber = idx + 1;
    this.totalBillCount = total;
    this.isFirstBill = idx === 0;
    this.isLastBill = idx >= total - 1;
    this.currentMeterNumber = this.state.getCurrentMeterNumber();
    this.totalMeterCount = this.state.getTotalMeters();

    // Check if this is the last bill in the current meter
    const meterBills = this.state.getBillsForCurrentMeter();
    const currentMeterGroupId = this.currentNav?.meterGroupId;
    if (currentMeterGroupId && meterBills.length > 0) {
      const lastBillInMeter = meterBills[meterBills.length - 1];
      this.isLastBillInMeter = lastBillInMeter.billGroupId === this.currentNav?.billGroupId;
    } else {
      this.isLastBillInMeter = true;
    }

    // Pre-compute button label
    if (this.isLastBill) {
      this.nextButtonLabel = 'Continue';
    } else if (this.isLastBillInMeter) {
      this.nextButtonLabel = 'Next Meter';
    } else {
      this.nextButtonLabel = 'Next Bill';
    }
  }

  // ===========================================================================
  // Form events
  // ===========================================================================

  /** Mirror of the form's live data — used as a save fallback. */
  onFormChanged(data: ParsedBillData): void {
    this.latestFormData = data;
  }

  /** Billing Method radio. Dual marks the bill as one half of separate
   *  utility/supplier billing and re-enables the section toggles in budgeting mode. */
  onBillingMethodChange(event: MatRadioChange): void {
    const value = event.value as BillType;
    if (value === this.billingMethod) return;
    this.billingMethod = value;
    const data = this.billFormRef()?.getFormData() ?? this.latestFormData;
    if (data) data.isDualBilling = value === BillType.DUAL;
    // The form reacts to the dualBilling input on the next CD pass (Consolidated
    // in budgeting mode forces both sides back on). Save after that pass so the
    // store copy and validation see the enforced flags, not the pre-switch ones.
    setTimeout(() => {
      if (!this.destroyed) this.saveFormData();
    });
  }

  /** The form asks the host to persist immediately (undo/revert actions). */
  onFormCommit(): void {
    this.saveFormData();
  }

  // ===========================================================================
  // Save / Navigation
  // ===========================================================================

  private saveFormData(): void {
    const groupId = this.currentNav?.billGroupId;
    const data = this.billFormRef()?.getFormData() ?? this.latestFormData;
    if (!groupId || !data) return;
    this.state.updateBillData(groupId, JSON.parse(JSON.stringify(data)));

    // For dual bills, flag the supplier bill as merged into the utility bill
    if (this.isDualBill && this.currentNav?.pairedBillFileId) {
      // Find the paired bill's groupId from its billFileId
      const pairedGroupId = this.findGroupIdForBillFileId(this.currentNav.pairedBillFileId);
      if (pairedGroupId) {
        this.state.setMergedBill(pairedGroupId, groupId);
      }
    }

    // Re-run field validation after every save
    this.revalidate();
  }

  /** Find the billGroupId that maps to a given billFileId */
  private findGroupIdForBillFileId(billFileId: string): string | null {
    const nav = this.state.getBillNavigation();
    for (const entry of nav) {
      if (entry.billFileId === billFileId) {
        return entry.billGroupId;
      }
    }
    return null;
  }

  navigateNext(): void {
    this.saveFormData();
    if (this.state.navigateToNextBill()) {
      this.loadCurrentBill();
    }
  }

  navigatePrev(): void {
    this.saveFormData();
    if (this.state.navigateToPrevBill()) {
      this.loadCurrentBill();
    }
  }

  goBack(): void {
    this.saveFormData();
    this.state.goBack();
  }

  onBackToOverview(): void {
    this.saveFormData();
    this.backToOverview.emit();
  }

  goForward(): void {
    this.saveFormData();

    const result = this.runValidation();
    this.rebuildFieldErrors(result);
    if (result.hasBlockers) return;

    this.state.completeStep('bill-review');
    const next = this.state.getNextStep();
    if (next) {
      this.state.goToStep(next);
    }
  }

  // --- Validation state (see guides/ai-onboarding/FIELD-VALIDATION.md) ---
  currentBillFieldErrors: Record<string, boolean> = {};
  currentBillFieldWarnings: Record<string, boolean> = {};
  validationSummary: ValidationSummary = { errorGroups: [], warningGroups: [], totalErrors: 0, totalWarnings: 0 };

  private runValidation(): StepValidationResult {
    const isBudgetingMode = this.billService.isBudgetingMode();
    return validateBillReviewStep({
      billData: this.state.billData(),
      billNavigation: this.state.billNavigation(),
      excludeBillGroupIds: this.state.skippedBillGroupIds(),
      isNewEntity: (gid) => this.state.isNewEntity(gid),
      meterContextFor: (nav) => {
        const ctx = this.getMeterContext(nav.meterGroupId);
        return {
          commodityType: ctx?.commodityType,
          isBudgetingMode,
        };
      },
      units: this.allUnits,
    });
  }

  private rebuildFieldErrors(result: StepValidationResult): void {
    const currentId = this.currentNav?.billGroupId;
    const curErr: Record<string, boolean> = {};
    const curWarn: Record<string, boolean> = {};
    for (const err of result.errors) {
      if (err.entityId === currentId) curErr[err.field] = true;
    }
    for (const warn of result.warnings) {
      if (warn.entityId === currentId) curWarn[warn.field] = true;
    }
    // New object references on purpose — the form watches these inputs to
    // keep its total-issue messaging in sync with the validation pass.
    this.currentBillFieldErrors = curErr;
    this.currentBillFieldWarnings = curWarn;

    this.validationSummary = {
      errorGroups: groupErrorsBySummary(result.errors),
      warningGroups: groupErrorsBySummary(result.warnings),
      totalErrors: result.errors.length,
      totalWarnings: result.warnings.length,
    };
  }

  revalidate(): void {
    this.rebuildFieldErrors(this.runValidation());
  }

  onNextClick(): void {
    if (this.isLastBill) {
      this.goForward();
    } else {
      this.navigateNext();
    }
  }

  // ===========================================================================
  // Meter context for the embedded form
  // ===========================================================================

  /**
   * Resolve the customerId from either the onboarding context or the bill AI flow selection.
   * The selectedCustomerId can be a number or a numeric string.
   */
  private getResolvedCustomerId(): number | null {
    const context = this.state.getContext();
    if (context?.customerId) {
      return parseInt(context.customerId, 10);
    }
    const selected = this.state.selectedCustomerId();
    if (selected == null) return null;
    const parsed = typeof selected === 'number' ? selected : parseInt(String(selected), 10);
    return isNaN(parsed) ? null : parsed;
  }

  /**
   * Look up state and commodity type for a meter from the current
   * match response so meter moves are honored, and prefer the user's
   * SA edits over the frozen match request when resolving the state.
   */
  private getMeterContext(meterGroupId: string): { state: string; commodityType: CommodityType } | null {
    return resolveMeterContext({
      meterGroupId,
      response: this.state.matchResponse(),
      request: this.state.matchRequest(),
      saEdits: this.state.saEdits(),
    });
  }

  /** Build the minimal entity context the bill form needs for energy-plan lookups. */
  private buildFormMeterContext(commodityType: CommodityType | undefined): BillFormMeterContext | null {
    const nav = this.currentNav;
    if (!nav) return null;

    const selections = this.state.entitySelections();
    const customerId = this.getResolvedCustomerId();
    const saIdRaw = selections[nav.saGroupId];
    const meterIdRaw = selections[nav.meterGroupId];
    const saId = saIdRaw != null ? Number(saIdRaw) : NaN;
    const meterId = meterIdRaw != null ? Number(meterIdRaw) : NaN;
    const ctx = this.getMeterContext(nav.meterGroupId);

    return {
      customerId: customerId ?? undefined,
      serviceAddressId: !isNaN(saId) ? saId : undefined,
      meterId: !isNaN(meterId) ? meterId : undefined,
      state: ctx?.state || undefined,
      commodityType,
      planFinderKey: nav.billGroupId,
    };
  }
}
